import { useForm } from '@tanstack/react-form'
import { useAddChurchMutation } from '../hooks/useAddChurchMutation'
import { useUpdateChurchMutation } from '../hooks/useUpdateChurchMutation'
import { ChurchCreateSchema, ChurchUpdateSchema } from '../schema/churchSchema'
import { useChurchesStore } from '../store/churchesStore'
import type { ApiError } from '@/types/api'
import type { ChurchUpdateInput } from '../types/church'
import { AddressInput } from '@/components/custom/address-input'
import { AutoCompleteSelect } from '@/components/custom/auto-complete-select'
import { Modal } from '@/components/custom/modal'
import { FormInput } from '@/components/custom/form-input'
import LoadingButton from '@/components/custom/loading-button'
import { PhoneInput } from '@/components/custom/phone-input'
import { Switch } from '@/components/custom/switch'
import { useUsersStore } from '@/features/users/store/usersStore'
// import { useEmailValidation } from '@/hooks'
import { notifyRequestError } from '@/utils/notifyRequestError'

const addDefaultValues = {
  name: '',
  address: { street: '', city: '', province: '', postal: '' },
  phone: '',
  email: '',
  pastorId: '',
}

export function ChurchForm({
  id,
  type,
  trigger,
  open,
  onClose,
}: {
  id?: string | null
  type: 'add' | 'edit'
  trigger?: React.ReactNode
  open?: boolean
  onClose?: () => void
}) {
  const { mutateAsync: addChurchMutation, isPending: isAdding } =
    useAddChurchMutation()
  const { mutateAsync: updateChurchMutation, isPending: isUpdating } =
    useUpdateChurchMutation()
  // const emailValidator = useEmailValidation()

  const churches = useChurchesStore((s) => s.churches)
  const initial = churches.find((c) => c._id === id)

  const users = useUsersStore((s) => s.users)
  const pastors = users.filter((u) => u.roles.includes('PASTOR'))

  const form = useForm({
    defaultValues:
      type === 'edit'
        ? {
            _id: initial?._id || '',
            name: initial?.name || '',
            address: {
              street: initial?.address.street || '',
              city: initial?.address.city || '',
              province: initial?.address.province || '',
              postal: initial?.address.postal || '',
            },
            phone: initial?.phone || '',
            email: initial?.email || '',
            pastorId: initial?.pastorId || '',
            active: initial?.active ?? false,
          }
        : addDefaultValues,
    onSubmit: async ({ value }) => {
      try {
        if (type === 'edit') {
          await updateChurchMutation({
            ...value,
            active: (value as ChurchUpdateInput).active ?? false,
          } as ChurchUpdateInput)
        } else {
          await addChurchMutation({
            ...value,
            address: {
              street: value.address.street,
              city: value.address.city,
              province: value.address.province,
              postal: value.address.postal,
            },
          })
        }
        onClose?.()
        form.reset()
      } catch (error) {
        console.log('Error occurred while submitting form:', error)
        notifyRequestError(
          error as ApiError,
          'Error occurred while submitting user form',
        )
      }
    },
  })

  const handleClose = () => {
    onClose?.()
    form.reset()
  }

  const schema = type === 'edit' ? ChurchUpdateSchema : ChurchCreateSchema

  const isPending = isAdding || isUpdating
  const buttonLabel = type === 'add' ? 'Add' : 'Update'
  const loadingLabel = type === 'add' ? 'Adding...' : 'Updating...'

  return (
    <Modal
      open={open}
      onClose={handleClose}
      trigger={trigger}
      title={type === 'edit' ? 'Edit Church' : 'Add Church'}
      direction="right"
      contentClassName="min-w-[98vw]"
    >
      <form
        id="church-form"
        onSubmit={(e) => {
          e.preventDefault()
          e.stopPropagation()
          form.handleSubmit()
        }}
        className="space-y-2"
      >
        <form.Field
          name="name"
          validators={{
            onBlur: ({ value }) => {
              const result = schema.shape.name.safeParse(value)
              return result.success ? undefined : result.error.errors[0].message
            },
          }}
          children={(field) => {
            return (
              <div className="gap-0">
                <FormInput
                  field={field}
                  label="Name"
                  type="text"
                  id={field.name}
                  name={field.name}
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                  required
                />
              </div>
            )
          }}
        />
        <form.Field
          name="address"
          validators={{
            onBlur: ({ value }) => {
              const result = schema.shape.address.safeParse(value)
              return result.success ? undefined : result.error.errors[0].message
            },
          }}
          children={(field) => {
            return (
              <div className="gap-0">
                <AddressInput
                  field={field}
                  label="Address"
                  onAddressSelect={(parsedAddress) => {
                    field.handleChange({
                      street: parsedAddress.street,
                      city: parsedAddress.city,
                      province: parsedAddress.province,
                      postal: parsedAddress.postal,
                    })
                  }}
                  placeholder="Start typing an address..."
                />
              </div>
            )
          }}
        />

        <form.Field
          name="phone"
          validators={{
            onBlur: ({ value }) => {
              if (!value) return undefined // optional
              const result = schema.shape.phone.safeParse(value)
              return result.success ? undefined : result.error.errors[0].message
            },
          }}
          children={(field) => {
            return (
              <div className="gap-0">
                <PhoneInput
                  name={field.name}
                  label="Phone Number"
                  value={field.state.value}
                  onChange={field.handleChange}
                  onBlur={field.handleBlur}
                  error={field.state.meta.errors[0]}
                />
              </div>
            )
          }}
        />

        <form.Field
          name="email"
          validators={
            {
              // onBlurAsync: async ({ value }) => {
              //   return await emailValidator({ value, isOptional: true })
              // },
            }
          }
          children={(field) => {
            return (
              <div className="gap-0">
                <FormInput
                  field={field}
                  label="Email"
                  type="email"
                  id={field.name}
                  name={field.name}
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                />
              </div>
            )
          }}
        />

        <form.Field
          name="pastorId"
          validators={{
            onBlur: ({ value }) => {
              const result = schema.shape.pastorId.safeParse(value)
              return result.success ? undefined : result.error.errors[0].message
            },
          }}
          children={(field) => {
            return (
              <div className="gap-0">
                <AutoCompleteSelect
                  field={field}
                  label="Pastor"
                  id={field.name}
                  name={field.name}
                  options={pastors.map((pastor) => ({
                    value: pastor._id,
                    label: pastor.name,
                  }))}
                  clearable
                />
              </div>
            )
          }}
        />

        {type === 'edit' && (
          <form.Field
            name="active"
            validators={{
              onBlur: ({ value }) => {
                const result = schema.safeParse({
                  ...form.state.values,
                  active: value,
                })
                return result.success
                  ? undefined
                  : result.error.errors.find((e) => e.path[0] === 'active')
                      ?.message
              },
            }}
            children={(field) => {
              return (
                <div className="gap-0">
                  <Switch
                    field={field}
                    label="Is church active?"
                    id={field.name}
                    name={field.name}
                  />
                </div>
              )
            }}
          />
        )}

        <form.Subscribe
          selector={(state) => [state.canSubmit]}
          children={([canSubmit]) => (
            <>
              <LoadingButton
                type="submit"
                className="w-full mt-4"
                isLoading={isPending}
                disabled={!canSubmit || isPending}
              >
                {isPending ? loadingLabel : buttonLabel}
              </LoadingButton>
            </>
          )}
        />
      </form>
    </Modal>
  )
}
